import React from 'react';
import api from './services/api';
import './InspectionHistory.css';
import { getAppPath } from './utils/runtimeMode';

export default function InspectionHistory() {
  const initialSearchParams = React.useMemo(() => new URLSearchParams(window.location.search), []);
  const legacyInspectionId = React.useMemo(() => String(initialSearchParams.get('id') || '').trim(), [initialSearchParams]);
  const initialTab = initialSearchParams.get('tab') === 'app' || legacyInspectionId ? 'app' : 'vehicle';
  const deepLinkedInspectionId = React.useMemo(() => String(initialSearchParams.get('inspectionId') || initialSearchParams.get('id') || '').trim(), [initialSearchParams]);
  const autoOpenedInspectionRef = React.useRef('');

  const [activeTab, setActiveTab] = React.useState(initialTab); // 'vehicle' | 'app'
  const [vehicleItems, setVehicleItems] = React.useState([]);
  const [appItems, setAppItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  // Vehicle filters
  const [vehStartDate, setVehStartDate] = React.useState('');
  const [vehEndDate, setVehEndDate] = React.useState('');
  const [vehTypeFilter, setVehTypeFilter] = React.useState('');
  const [vehUserFilter, setVehUserFilter] = React.useState('');
  const [vehPlateFilter, setVehPlateFilter] = React.useState('');
  const [vehHasDefFilter, setVehHasDefFilter] = React.useState(''); // '', 'yes', 'no'

  // Inspection App filters
  const [appStartDate, setAppStartDate] = React.useState('');
  const [appEndDate, setAppEndDate] = React.useState('');
  const [appCategoryFilter, setAppCategoryFilter] = React.useState('');
  const [appLocationFilter, setAppLocationFilter] = React.useState('');
  const [appHasDefFilter, setAppHasDefFilter] = React.useState(''); // '', 'yes', 'no'

  // Pagination per tab
  const [pageVehicle, setPageVehicle] = React.useState(1);
  const [pageApp, setPageApp] = React.useState(1);
  const pageSize = 10;

  React.useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        if (activeTab === 'vehicle') {
          const res = await api.get('/api/vehicleInspections');
          setVehicleItems(Array.isArray(res.data) ? res.data : []);
          setPageVehicle(1);
          // Reset filters when switching into vehicle tab
          setVehStartDate('');
          setVehEndDate('');
          setVehTypeFilter('');
          setVehUserFilter('');
          setVehPlateFilter('');
          setVehHasDefFilter('');
        } else {
          const res = await api.get('/api/inspectionReports');
          setAppItems(Array.isArray(res.data) ? res.data : []);
          setPageApp(1);
          // Reset app filters when switching into app tab
          setAppStartDate('');
          setAppEndDate('');
          setAppCategoryFilter('');
          setAppLocationFilter('');
          setAppHasDefFilter('');
        }
      } catch (err) {
        console.error('Failed to fetch inspection history:', err);
        setError('Unable to load inspection history.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [activeTab]);

  const fmtBool = (v) => v ? 'Yes' : 'No';

  // Server-side PDFs are used for viewing; no client print view needed

  const handleViewVehicle = async (item) => {
    const id = item.id || item.ID || item.INSPECTION_ID;
    if (!id) return;
    const url = `/api/vehicleInspections/pdf/${id}`;
    try {
      const resp = await api.get(url, { responseType: 'blob' });
      const ct = resp.headers['content-type'] || '';
      if (!ct.includes('application/pdf')) {
        // Try to read as text for error details
        const text = await resp.data.text?.().catch(() => null);
        console.warn('Unexpected content-type for PDF response:', ct, text);
        if (resp.status === 440 || resp.status === 401) {
          window.location.href = getAppPath('/login');
          return;
        }
        alert('Unable to open PDF. Please try again.');
        return;
      }
      const blobUrl = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const w = window.open(blobUrl, '_blank', 'noopener,noreferrer');
      if (!w) {
        // If popup blocked, provide guidance via alert instead of hijacking current tab.
        alert('Popup blocked. Allow popups for this site to view the PDF in a new tab.');
      }
    } catch (err) {
      console.error('Failed to fetch vehicle PDF:', err);
      if (err?.response?.status === 440 || err?.response?.status === 401) {
        window.location.href = getAppPath('/login');
        return;
      }
      alert('Failed to fetch PDF.');
    }
  };

  React.useEffect(() => {
    if (activeTab !== 'vehicle' || !deepLinkedInspectionId || vehicleItems.length === 0) {
      return;
    }

    if (autoOpenedInspectionRef.current === deepLinkedInspectionId) {
      return;
    }

    const matchingInspection = vehicleItems.find((item) => String(item.id || item.ID || item.INSPECTION_ID || '').trim() === deepLinkedInspectionId);
    if (!matchingInspection) {
      return;
    }

    autoOpenedInspectionRef.current = deepLinkedInspectionId;
    handleViewVehicle(matchingInspection);
  }, [activeTab, deepLinkedInspectionId, vehicleItems]);

  const handleViewApp = async (item) => {
    const id = item.INSPECTION_ID || item.id;
    if (!id) return;
    const url = `/api/inspectionReports/pdf/${id}`;
    try {
      const resp = await api.get(url, { responseType: 'blob' });
      const ct = resp.headers['content-type'] || '';
      if (!ct.includes('application/pdf')) {
        const text = await resp.data.text?.().catch(() => null);
        console.warn('Unexpected content-type for PDF response:', ct, text);
        if (resp.status === 440 || resp.status === 401) {
          window.location.href = getAppPath('/login');
          return;
        }
        alert('Unable to open PDF. Please try again.');
        return;
      }
      const blobUrl = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const w = window.open(blobUrl, '_blank', 'noopener,noreferrer');
      if (!w) {
        alert('Popup blocked. Allow popups for this site to view the PDF in a new tab.');
      }
    } catch (err) {
      console.error('Failed to fetch inspection report PDF:', err);
      if (err?.response?.status === 440 || err?.response?.status === 401) {
        window.location.href = getAppPath('/login');
        return;
      }
      alert('Failed to fetch PDF.');
    }
  };

  React.useEffect(() => {
    if (activeTab !== 'app' || !deepLinkedInspectionId || appItems.length === 0) {
      return;
    }

    if (autoOpenedInspectionRef.current === deepLinkedInspectionId) {
      return;
    }

    const matchingInspection = appItems.find((item) => String(item.INSPECTION_ID || item.id || '').trim() === deepLinkedInspectionId);
    if (!matchingInspection) {
      return;
    }

    autoOpenedInspectionRef.current = deepLinkedInspectionId;
    handleViewApp(matchingInspection);
  }, [activeTab, deepLinkedInspectionId, appItems]);

  // Pagination helpers
  // Build vehicle filter option lists from current data
  const vehTypeOptions = Array.from(new Set(
    vehicleItems
      .map(r => (r.vehicleType || r.VEHICLE_TYPE || '').trim())
      .filter(Boolean)
  )).sort();

  const vehUserOptions = Array.from(new Set(
    vehicleItems
      .map(r => (r.inspectorName || r.INSPECTOR_NAME || r.USER || '').trim())
      .filter(Boolean)
  )).sort();

  const vehPlateOptions = Array.from(new Set(
    vehicleItems
      .map(r => (r.plate || r.PLATE || '').trim())
      .filter(Boolean)
  )).sort();

  // Apply filters client-side
  const filteredVehicleItems = vehicleItems.filter(r => {
    let ok = true;

    const dateStr = r.inspectionDate || r.DATE;
    const timeStr = r.inspectionTime || r.TIME || '00:00:00';
    const fullDateTime = dateStr ? new Date(`${dateStr}T${timeStr}`) : null;

    const sameOrAfter = (d, cmp) => {
      if (!d || !cmp) return true;
      const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const cOnly = new Date(cmp.getFullYear(), cmp.getMonth(), cmp.getDate());
      return dOnly >= cOnly;
    };

    const sameOrBefore = (d, cmp) => {
      if (!d || !cmp) return true;
      const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const cOnly = new Date(cmp.getFullYear(), cmp.getMonth(), cmp.getDate());
      return dOnly <= cOnly;
    };

    if (vehStartDate && fullDateTime) {
      const start = new Date(vehStartDate);
      ok = ok && sameOrAfter(fullDateTime, start);
    }

    if (vehEndDate && fullDateTime) {
      const end = new Date(vehEndDate);
      ok = ok && sameOrBefore(fullDateTime, end);
    }

    const vType = (r.vehicleType || r.VEHICLE_TYPE || '').trim();
    if (vehTypeFilter) {
      ok = ok && vType === vehTypeFilter;
    }

    const vUser = (r.inspectorName || r.INSPECTOR_NAME || r.USER || '').trim();
    if (vehUserFilter) {
      ok = ok && vUser === vehUserFilter;
    }

    const vPlate = (r.plate || r.PLATE || '').trim();
    if (vehPlateFilter) {
      ok = ok && vPlate === vehPlateFilter;
    }

    const hasDef = !!(r.hasDeficiencies ?? r.HAS_DEFICIENCIES);
    if (vehHasDefFilter === 'yes') {
      ok = ok && hasDef;
    } else if (vehHasDefFilter === 'no') {
      ok = ok && !hasDef;
    }

    return ok;
  });

  const vehicleTotal = filteredVehicleItems.length;
  const vehiclePages = Math.max(1, Math.ceil(vehicleTotal / pageSize));
  const vehicleSlice = filteredVehicleItems.slice((pageVehicle - 1) * pageSize, pageVehicle * pageSize);

  // Build Inspection App filter option lists from current data
  const appCategoryOptions = Array.from(new Set(
    appItems
      .map(r => (r.CATEGORY || r.category || '').trim())
      .filter(Boolean)
  )).sort();

  const appLocationOptions = Array.from(new Set(
    appItems
      .map(r => {
        const addr = (r.LOCATION_ADDRESS || r.address || '').trim();
        const name = (r.LOCATION_NAME || r.name || '').trim();
        const combined = [addr, name].filter(Boolean).join(' — ');
        return combined;
      })
      .filter(Boolean)
  )).sort();

  // Apply filters to Inspection App items
  const filteredAppItems = appItems.filter(r => {
    let ok = true;

    const dateStr = r.INSPECTION_DATE || r.date;
    const timeStr = r.INSPECTION_TIME || r.time || '00:00:00';
    const fullDateTime = dateStr ? new Date(`${dateStr}T${timeStr}`) : null;

    const sameOrAfter = (d, cmp) => {
      if (!d || !cmp) return true;
      const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const cOnly = new Date(cmp.getFullYear(), cmp.getMonth(), cmp.getDate());
      return dOnly >= cOnly;
    };

    const sameOrBefore = (d, cmp) => {
      if (!d || !cmp) return true;
      const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const cOnly = new Date(cmp.getFullYear(), cmp.getMonth(), cmp.getDate());
      return dOnly <= cOnly;
    };

    if (appStartDate && fullDateTime) {
      const start = new Date(appStartDate);
      ok = ok && sameOrAfter(fullDateTime, start);
    }

    if (appEndDate && fullDateTime) {
      const end = new Date(appEndDate);
      ok = ok && sameOrBefore(fullDateTime, end);
    }

    const cat = (r.CATEGORY || r.category || '').trim();
    if (appCategoryFilter) {
      ok = ok && cat === appCategoryFilter;
    }

    const loc = [
      (r.LOCATION_ADDRESS || r.address || '').trim(),
      (r.LOCATION_NAME || r.name || '').trim()
    ].filter(Boolean).join(' — ');
    if (appLocationFilter) {
      ok = ok && loc === appLocationFilter;
    }

    const hasDef = !!(r.HAS_DEFICIENCIES ?? r.hasDeficiencies);
    if (appHasDefFilter === 'yes') {
      ok = ok && hasDef;
    } else if (appHasDefFilter === 'no') {
      ok = ok && !hasDef;
    }

    return ok;
  });

  const appTotal = filteredAppItems.length;
  const appPages = Math.max(1, Math.ceil(appTotal / pageSize));
  const appSlice = filteredAppItems.slice((pageApp - 1) * pageSize, pageApp * pageSize);

  return (
    <div className="inspection-history">
      <h2>Inspection History</h2>
      <div className="tabs">
        <button className={`tab ${activeTab === 'vehicle' ? 'active' : ''}`} onClick={() => setActiveTab('vehicle')}>Vehicle Inspections</button>
        <button className={`tab ${activeTab === 'app' ? 'active' : ''}`} onClick={() => setActiveTab('app')}>Facility Inspections</button>
      </div>

      {loading && <div>Loading…</div>}
      {error && <div className="error">{error}</div>}

      {activeTab === 'vehicle' && !loading && (
        <>
          {/* Vehicle filters */}
          <div className="history-filters" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }}>
            <div>
              <label style={{ display: 'block', fontWeight: 500 }}>Start Date</label>
              <input
                type="date"
                value={vehStartDate}
                onChange={(e) => { setVehStartDate(e.target.value); setPageVehicle(1); }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 500 }}>End Date</label>
              <input
                type="date"
                value={vehEndDate}
                onChange={(e) => { setVehEndDate(e.target.value); setPageVehicle(1); }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 500 }}>Vehicle Type</label>
              <select
                value={vehTypeFilter}
                onChange={(e) => { setVehTypeFilter(e.target.value); setPageVehicle(1); }}
              >
                <option value="">All</option>
                {vehTypeOptions.map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 500 }}>User</label>
              <select
                value={vehUserFilter}
                onChange={(e) => { setVehUserFilter(e.target.value); setPageVehicle(1); }}
              >
                <option value="">All</option>
                {vehUserOptions.map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 500 }}>Plate</label>
              <select
                value={vehPlateFilter}
                onChange={(e) => { setVehPlateFilter(e.target.value); setPageVehicle(1); }}
              >
                <option value="">All</option>
                {vehPlateOptions.map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 500 }}>Deficiencies</label>
              <select
                value={vehHasDefFilter}
                onChange={(e) => { setVehHasDefFilter(e.target.value); setPageVehicle(1); }}
              >
                <option value="">All</option>
                <option value="yes">With deficiencies</option>
                <option value="no">No deficiencies</option>
              </select>
            </div>
          </div>

          <table className="history-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Time</th>
                <th>Department</th>
                <th>Vehicle</th>
                <th>Plate</th>
                <th>Deficiencies</th>
                <th>PDF</th>
              </tr>
            </thead>
            <tbody>
              {vehicleSlice.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center' }}>No records</td></tr>
              ) : vehicleSlice.map((r) => (
                <tr key={r.id || r.ID || r.INSPECTION_ID}>
                  <td>{r.inspectionDate || r.DATE}</td>
                  <td>{r.inspectionTime || r.TIME}</td>
                  <td>{r.department || r.DEPARTMENT}</td>
                  <td>{(r.vehicleType || r.VEHICLE_TYPE) || ''}</td>
                  <td>{r.plate || r.PLATE || ''}</td>
                  <td>{fmtBool(r.hasDeficiencies ?? r.HAS_DEFICIENCIES)}</td>
                  <td><button onClick={() => handleViewVehicle(r)}>View PDF</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pager">
            <div>
              <button onClick={() => setPageVehicle(p => Math.max(1, p - 1))} disabled={pageVehicle <= 1}>Prev</button>
              <button onClick={() => setPageVehicle(p => Math.min(vehiclePages, p + 1))} disabled={pageVehicle >= vehiclePages} style={{ marginLeft: 8 }}>Next</button>
            </div>
            <div>Page {pageVehicle} of {vehiclePages} — {vehicleTotal} records</div>
          </div>
        </>
      )}

      {activeTab === 'app' && !loading && (
        <>
          {/* Inspection App filters */}
          <div className="history-filters" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }}>
            <div>
              <label style={{ display: 'block', fontWeight: 500 }}>Start Date</label>
              <input
                type="date"
                value={appStartDate}
                onChange={(e) => { setAppStartDate(e.target.value); setPageApp(1); }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 500 }}>End Date</label>
              <input
                type="date"
                value={appEndDate}
                onChange={(e) => { setAppEndDate(e.target.value); setPageApp(1); }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 500 }}>Category</label>
              <select
                value={appCategoryFilter}
                onChange={(e) => { setAppCategoryFilter(e.target.value); setPageApp(1); }}
              >
                <option value="">All</option>
                {appCategoryOptions.map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 500 }}>Location</label>
              <select
                value={appLocationFilter}
                onChange={(e) => { setAppLocationFilter(e.target.value); setPageApp(1); }}
              >
                <option value="">All</option>
                {appLocationOptions.map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 500 }}>Deficiencies</label>
              <select
                value={appHasDefFilter}
                onChange={(e) => { setAppHasDefFilter(e.target.value); setPageApp(1); }}
              >
                <option value="">All</option>
                <option value="yes">With deficiencies</option>
                <option value="no">No deficiencies</option>
              </select>
            </div>
          </div>

          <table className="history-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Time</th>
                <th>Category</th>
                <th>Location</th>
                <th>Deficiencies</th>
                <th>PDF</th>
              </tr>
            </thead>
            <tbody>
              {appSlice.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center' }}>No records</td></tr>
              ) : appSlice.map((r) => (
                <tr key={r.INSPECTION_ID || r.id}>
                  <td>{r.INSPECTION_DATE || r.date}</td>
                  <td>{r.INSPECTION_TIME || r.time}</td>
                  <td>{r.CATEGORY || r.category}</td>
                  <td>{[r.LOCATION_ADDRESS || r.address, r.LOCATION_NAME || r.name].filter(Boolean).join(' — ')}</td>
                  <td>{fmtBool(r.HAS_DEFICIENCIES ?? r.hasDeficiencies)}</td>
                  <td><button onClick={() => handleViewApp(r)}>View PDF</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pager">
            <div>
              <button onClick={() => setPageApp(p => Math.max(1, p - 1))} disabled={pageApp <= 1}>Prev</button>
              <button onClick={() => setPageApp(p => Math.min(appPages, p + 1))} disabled={pageApp >= appPages} style={{ marginLeft: 8 }}>Next</button>
            </div>
            <div>Page {pageApp} of {appPages} — {appTotal} records</div>
          </div>
        </>
      )}
    </div>
  );
}
